/*
 * Date, time and number formatting. These are plain functions used everywhere, so the
 * user's locale, clock, date-format and timestamp preferences are held at module level
 * and set once per render of the app shell via `setFormatPrefs`. All names (months,
 * weekdays, "yesterday") come from Intl in the chosen locale.
 */

export type ClockPref = "24h" | "12h";
export type DateFormatPref = "dmy" | "mdy" | "ymd";
export type TimestampPref = "relative" | "absolute";
export interface FormatPrefs {
  clock: ClockPref;
  dateFormat: DateFormatPref;
  locale: string;
  timestamps: TimestampPref;
  showSeconds: boolean;
}

let prefs: FormatPrefs = { clock: "24h", dateFormat: "dmy", locale: "en-GB", timestamps: "relative", showSeconds: false };

function build(p: FormatPrefs) {
  const L = p.locale;
  const timeOpts: Intl.DateTimeFormatOptions =
    p.clock === "12h" ? { hour: "numeric", minute: "2-digit", hour12: true } : { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  return {
    rtf: new Intl.RelativeTimeFormat(L, { numeric: "auto" }),
    rtfShort: new Intl.RelativeTimeFormat(L, { numeric: "always", style: "narrow" }),
    monthYear: new Intl.DateTimeFormat(L, { month: "long", year: "numeric" }),
    weekday: new Intl.DateTimeFormat(L, { weekday: "long" }),
    shortMonth: new Intl.DateTimeFormat(L, { month: "short" }),
    longMonth: new Intl.DateTimeFormat(L, { month: "long" }),
    time: new Intl.DateTimeFormat(L, timeOpts),
    timeSec: new Intl.DateTimeFormat(L, { ...timeOpts, second: "2-digit" }),
    compact: new Intl.NumberFormat(L, { notation: "compact", maximumFractionDigits: 1 }),
    number: new Intl.NumberFormat(L),
  };
}

let f = build(prefs);

/** Idempotent; cheap to call on every render. Missing fields keep their current value. */
export function setFormatPrefs(next: Partial<FormatPrefs>) {
  const merged = { ...prefs, ...next };
  if (merged.clock === prefs.clock && merged.dateFormat === prefs.dateFormat && merged.locale === prefs.locale && merged.timestamps === prefs.timestamps && merged.showSeconds === prefs.showSeconds) return;
  const rebuild = merged.locale !== prefs.locale || merged.clock !== prefs.clock;
  prefs = merged;
  if (rebuild) f = build(prefs);
}

export const getFormatPrefs = (): FormatPrefs => prefs;

const isEnglish = () => prefs.locale.startsWith("en");
const cap = (s: string) => s.charAt(0).toLocaleUpperCase(prefs.locale) + s.slice(1);
const pad = (n: number) => String(n).padStart(2, "0");

/** "12/03/2025", "03/12/2025" or "2025-03-12". */
export function numericDate(d: Date, fmt: DateFormatPref = prefs.dateFormat): string {
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  if (fmt === "mdy") return `${mm}/${dd}/${yyyy}`;
  if (fmt === "ymd") return `${yyyy}-${mm}-${dd}`;
  return `${dd}/${mm}/${yyyy}`;
}

/** "12 Mar" / "Mar 12". */
function dayMonth(d: Date, month: Intl.DateTimeFormat = f.shortMonth): string {
  const m = month.format(d);
  return prefs.dateFormat === "dmy" ? `${d.getDate()} ${m}` : `${m} ${d.getDate()}`;
}

/** "12 Mar 2024" / "Mar 12, 2024" / "2024-03-12". */
function dayMonthYear(d: Date, month: Intl.DateTimeFormat = f.shortMonth): string {
  if (prefs.dateFormat === "ymd") return numericDate(d, "ymd");
  if (prefs.dateFormat === "mdy") return `${month.format(d)} ${d.getDate()}, ${d.getFullYear()}`;
  return `${d.getDate()} ${month.format(d)} ${d.getFullYear()}`;
}

const time = (d: Date) => (prefs.showSeconds ? f.timeSec : f.time).format(d);
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** "Today" / "Yesterday" (or the locale's words). */
function todayWord(offset: 0 | -1) {
  if (isEnglish()) return offset === 0 ? "Today" : "Yesterday";
  return cap(f.rtf.format(offset, "day"));
}

/** Absolute short stamp: time today, otherwise date (and year when not this year). */
function absoluteShort(d: Date): string {
  const now = new Date();
  if (sameDay(d, now)) return time(d);
  return d.getFullYear() === now.getFullYear() ? dayMonth(d) : dayMonthYear(d);
}

/** Compact time for feeds: "now", "4m", "3h", "12 Mar", "12 Mar 2024" (absolute style: "14:02" or the date). */
export function timeAgo(iso: string): string {
  const d = new Date(iso);
  if (prefs.timestamps === "absolute") return absoluteShort(d);
  const s = (Date.now() - d.getTime()) / 1000;
  if (isEnglish()) {
    if (s < 45) return "now";
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    if (s < 7 * 86400) return `${Math.round(s / 86400)}d`;
  } else {
    if (s < 45) return cap(f.rtf.format(0, "second"));
    if (s < 3600) return f.rtfShort.format(-Math.round(s / 60), "minute");
    if (s < 86400) return f.rtfShort.format(-Math.round(s / 3600), "hour");
    if (s < 7 * 86400) return f.rtfShort.format(-Math.round(s / 86400), "day");
  }
  return d.getFullYear() === new Date().getFullYear() ? dayMonth(d) : dayMonthYear(d);
}

/** "in 5 minutes", "3 hours ago" — or the absolute date/time with the absolute style. */
export function relative(iso: string): string {
  if (prefs.timestamps === "absolute") return chatTimestamp(iso);
  return relativeAlways(iso);
}

/** Relative regardless of the timestamp preference (countdowns, expiries). */
export function relativeAlways(iso: string): string {
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(s);
  if (abs < 60) return f.rtf.format(Math.round(s), "second");
  if (abs < 3600) return f.rtf.format(Math.round(s / 60), "minute");
  if (abs < 86400) return f.rtf.format(Math.round(s / 3600), "hour");
  return f.rtf.format(Math.round(s / 86400), "day");
}

/** "Tuesday 12 March 2024 at 14:02". */
export function fullDate(iso: string): string {
  const d = new Date(iso);
  const sep = prefs.dateFormat === "dmy" ? " " : ", ";
  const at = isEnglish() ? " at " : ", ";
  return `${cap(f.weekday.format(d))}${sep}${dayMonthYear(d, f.longMonth)}${at}${time(d)}`;
}
export const clock = (iso: string) => time(new Date(iso));
export const joinedDate = (iso: string) => f.monthYear.format(new Date(iso));

/** Chat timestamps: "Today at 14:02", "Yesterday at 09:10", "12/03/2025 14:02". */
export function chatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const at = isEnglish() ? " at " : " ";
  if (sameDay(d, now)) return `${todayWord(0)}${at}${time(d)}`;
  if (sameDay(d, yesterday)) return `${todayWord(-1)}${at}${time(d)}`;
  return `${numericDate(d)} ${time(d)}`;
}

export function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return todayWord(0);
  if (sameDay(d, yesterday)) return todayWord(-1);
  const sep = prefs.dateFormat === "dmy" ? " " : ", ";
  const body = d.getFullYear() === now.getFullYear() && prefs.dateFormat !== "ymd" ? dayMonth(d, f.longMonth) : dayMonthYear(d, f.longMonth);
  return `${cap(f.weekday.format(d))}${sep}${body}`;
}

export const compact = (n: number) => (n < 1000 ? f.number.format(n) : f.compact.format(n));
export const formatNumber = (n: number) => f.number.format(n);

export const plural = (n: number, one: string, many = `${one}s`) => `${compact(n)} ${n === 1 ? one : many}`;
